/**
 * SSE Stream Parser — Reusable Server-Sent Events parser
 *
 * Parses SSE streams from OpenAI-compatible APIs
 * Also reusable for MCP SSE transport.
 */

// ────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────

export interface SSEDelta {
    content?: string
    role?: string
    images?: Array<{ type: 'image_url'; image_url: { url: string } }>
    reasoning_content?: unknown
    reasoning_text?: unknown
    thinking?: unknown
    thinking_content?: unknown
    reasoning?: unknown
    reasoning_details?: unknown
}

export interface SSEChoice {
    index: number
    delta: SSEDelta
    finish_reason: string | null
}

export interface SSEChunk {
    id: string
    choices: SSEChoice[]
    model?: string
    usage?: {
        prompt_tokens: number
        completion_tokens: number
        total_tokens?: number
    }
}

export interface SSEEvent<T = unknown> {
    event?: string
    data: T
}

// ────────────────────────────────────────────────────────────────
// Parser
// ────────────────────────────────────────────────────────────────

/**
 * Parse an SSE stream from a ReadableStream into typed chunks.
 * Handles buffering partial lines and `data: [DONE]` sentinel.
 */
export async function* parseSSEEvents(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    signal?: AbortSignal,
): AsyncIterableIterator<SSEEvent<string>> {
    const decoder = new TextDecoder()
    let buffer = ''
    let eventName = ''
    let dataLines: string[] = []

    const flush = (): SSEEvent<string> | null => {
        if (!eventName && dataLines.length === 0) return null
        const data = dataLines.join('\n')
        const event = eventName.trim() || undefined
        eventName = ''
        dataLines = []
        return { event, data }
    }

    try {
        while (true) {
            if (signal?.aborted) return

            const { done, value } = await reader.read()
            if (done) {
                const pending = flush()
                if (pending) yield pending
                break
            }

            buffer += decoder.decode(value, { stream: true })

            const lines = buffer.split(/\r?\n/)
            buffer = lines.pop() ?? ''

            for (const line of lines) {
                if (line === '') {
                    const event = flush()
                    if (event) {
                        if (event.data === '[DONE]') return
                        yield event
                    }
                    continue
                }

                if (line.startsWith(':')) continue
                if (line.startsWith('event:')) {
                    eventName = line.slice(6).trim()
                    continue
                }
                if (line.startsWith('data:')) {
                    dataLines.push(line.slice(5).replace(/^ /, ''))
                }
            }
        }
    } finally {
        reader.releaseLock()
    }
}

export async function* parseSSEStream<T = SSEChunk>(
    reader: ReadableStreamDefaultReader<Uint8Array>,
    signal?: AbortSignal,
): AsyncIterableIterator<T> {
    for await (const event of parseSSEEvents(reader, signal)) {
        try {
            const parsed = JSON.parse(event.data) as T
            if (event.event && parsed && typeof parsed === 'object' && !Array.isArray(parsed) && !('type' in (parsed as Record<string, unknown>))) {
                ;(parsed as Record<string, unknown>).type = event.event
            }
            yield parsed
        } catch {
            console.warn('[SSEParser] Skipping malformed SSE chunk:', event.data.slice(0, 100))
        }
    }
}
